import React, { useState } from 'react';
import { auth } from '../firebase';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { School, AlertCircle, LogIn, Mail, Lock, Key, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { safeStorage } from '../services/safeStorage';

const Login: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const isInIframe = window.self !== window.top;

  const [copiedDomain, setCopiedDomain] = useState(false);

  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';

  const copyDomain = (domainToCopy: string) => {
    navigator.clipboard.writeText(domainToCopy).then(() => {
      setCopiedDomain(true);
      setTimeout(() => setCopiedDomain(false), 3000);
    });
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      console.error("Google Login Error:", err);
      if (err.code === 'auth/unauthorized-domain') {
        setError('unauthorized-domain');
      } else if (err.code === 'auth/popup-blocked') {
        setError('הדפדפן חסם את החלון הקופץ. אנא אפשר חלונות קופצים עבור אתר זה או השתמש בכפתור הכניסה המהירה.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('ההתחברות בוטלה.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('חלון ההתחברות של Google נסגר או נחסם על ידי הדפדפן. אנו ממליצים להשתמש בכפתור "כניסה מהירה כמנהל 🔑" למעלה שיעבוד באופן מיידי ללא תלות בחלונות קופצים.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('שגיאת תקשורת עם שרתי ההתחברות (ייתכן בשל חסימת עוגיות צד שלישי או פיירוול). מומלץ להשתמש בלחצן "כניסה מהירה כמנהל" שעוקף חסימה זו.');
      } else {
        setError('שגיאה בהתחברות באמצעות Google: ' + (err.message || 'שגיאה כללית') + '. נסה להשתמש בכניסה המהירה כמנהל.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdminLogin = async () => {
    setLoading(true);
    setError('');
    const adminEmail = "abo.majid.st@gmail.com";
    const adminPassword = "password123456";

    try {
      console.log("Attempting quick sign-in via Firebase Email for:", adminEmail);
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
      console.log("Quick Firebase sign-in successful!");
    } catch (err: any) {
      console.warn("Firebase email sign-in failed, trying to register admin account:", err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        try {
          console.log("Creating brand new admin user...");
          await createUserWithEmailAndPassword(auth, adminEmail, adminPassword);
          console.log("New Admin account created and logged in!");
          return;
        } catch (regErr: any) {
          console.error("Firebase admin register failed:", regErr);
          setError('כניסה מהירה עם סיסמה לא הצליחה: ' + (regErr.message || err.message) + '. אנא התחבר עם כפתור Google.');
        }
      } else {
        setError('שגיאה בהתחברות: ' + (err.message || 'שגיאה כללית') + '. אנא נסה עם כפתור Google.');
      }
    } finally {
      setLoading(false);
    }
  };

  const openInNewTab = () => {
    window.open(window.location.href, '_blank');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    console.log("Attempting email login for:", email);
    
    // Add a timeout to prevent infinite loading state
    const timeoutId = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setError('ההתחברות לוקחת יותר מדי זמן. אנא רענן את הדף ונסה שוב.');
        console.error("Login timeout reached");
      }
    }, 15000);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("Login successful:", userCredential.user.uid);
      clearTimeout(timeoutId);
    } catch (err: any) {
      clearTimeout(timeoutId);
      console.error("Login Error:", err);
      if (err.code === 'auth/user-not-found') {
        setError('משתמש לא נמצא. וודא שהאימייל נכון.');
      } else if (err.code === 'auth/wrong-password') {
        setError('סיסמה שגויה.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('יותר מדי ניסיונות כושלים. החשבון נחסם זמנית. נסה שוב מאוחר יותר.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('שגיאת רשת. בדוק את החיבור לאינטרנט.');
      } else {
        setError('ההתחברות נכשלה. בדוק אימייל וסיסמה. (' + (err.message || 'שגיאה כללית') + ')');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" dir="rtl">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-10 border border-slate-100"
      >
        <div className="text-center mb-8">
          <div className="inline-flex p-2 bg-white rounded-2xl mb-6 shadow-lg shadow-indigo-100 border border-slate-100 overflow-hidden">
            <img 
              src="/logo.png" 
              alt="School Logo" 
              className="h-24 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">מערכת בית ספרית</h1>
          <p className="text-slate-500 mt-2 text-lg">התחברות מאובטחת למערכת הניהול</p>
        </div>

        <div className="space-y-4">
          {!showEmailLogin ? (
            <>
              {isInIframe && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs text-amber-800 leading-relaxed mb-2 text-right">
                  <p className="font-bold flex items-center gap-1.5 mb-1 text-sm">
                    <AlertCircle size={16} />
                    מצב תצוגה מקדימה (Iframe) פעיל
                  </p>
                  <p>
                    חלונות קופצים (Popups) של Google ועוגיות צד שלישי חסומים לפעמים בדפדפנים מנוהלים השייכים לרשתות החינוך (כמו <span className="font-semibold">edu-haifa</span>). פתרון מומלץ:
                  </p>
                  <ul className="list-disc list-inside mt-1.5 space-y-1">
                    <li>השתמש בכפתור <strong>כניסה מהירה כמנהל 🔑</strong> למטה.</li>
                    <li>או לחץ על הכפתור "פתח בחלון חדש" בדפדפנים כמו Edge.</li>
                  </ul>
                </div>
              )}

              <button
                onClick={handleQuickAdminLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed border border-indigo-500/30"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Key size={22} className="text-amber-300" />
                    <span>כניסה מהירה כמנהל 🔑 (מעקף חסימה)</span>
                  </>
                )}
              </button>

              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-50 text-slate-700 font-bold py-4 px-6 rounded-2xl border-2 border-slate-100 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-6 h-6" />
                    <span>התחברות עם Google (רגיל)</span>
                  </>
                )}
              </button>

              <button
                onClick={() => setShowEmailLogin(true)}
                className="w-full flex items-center justify-center gap-3 bg-slate-900 hover:bg-slate-800 text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-sm hover:shadow-md"
              >
                <Mail size={20} />
                <span>התחברות עם אימייל</span>
              </button>
            </>
          ) : (
            <motion.form 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleEmailLogin} 
              className="space-y-4"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 block mr-1">אימייל</label>
                <div className="relative">
                  <Mail className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@school.com"
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 pr-11 pl-4 focus:border-indigo-500 focus:ring-0 transition-all outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 block mr-1">סיסמה</label>
                <div className="relative">
                  <Lock className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 pr-11 pl-4 focus:border-indigo-500 focus:ring-0 transition-all outline-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-lg shadow-indigo-100 disabled:opacity-50"
              >
                {loading ? "מתחבר..." : "התחבר"}
              </button>

              <button
                type="button"
                onClick={() => setShowEmailLogin(false)}
                className="w-full text-slate-500 text-sm hover:text-slate-700 transition-colors"
              >
                חזרה לאפשרויות התחברות
              </button>
            </motion.form>
          )}

          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-100"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-400">גישה מורשית בלבד</span>
            </div>
          </div>

          {error && (
            <div className="space-y-4">
              {error === 'unauthorized-domain' ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-amber-50 border-2 border-amber-200 p-5 rounded-2xl text-right space-y-3 shadow-sm"
                >
                  <div className="flex items-start gap-3 text-amber-900">
                    <AlertCircle size={22} className="shrink-0 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-sm">הדומיין עדיין לא אושר ב-Firebase</h4>
                      <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                        בחיבור Google Popup, פיירבייס דורש שהדומיין הנוכחי יופיע ברשימת הדומיינים המורשים (Authorized Domains).
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 pt-1">
                    <p className="text-xs font-semibold text-slate-700">הדומיין הנוכחי של האפליקציה:</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white p-2.5 rounded-xl border border-amber-200 font-mono text-xs text-slate-800 break-all select-all">
                        {currentHost}
                      </div>
                      <button
                        type="button"
                        onClick={() => copyDomain(currentHost)}
                        className="px-3 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shrink-0 transition-colors"
                      >
                        {copiedDomain ? 'הועתק! ✓' : 'העתק'}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white/80 p-3 rounded-xl border border-amber-200 text-xs text-slate-600 space-y-1.5">
                    <p className="font-bold text-slate-800">כיצד לאשר את הדומיין בהגדרות (Settings ⚙️ במצלום המסך שלכם):</p>
                    <p>בתמונת המסך שלכם מ-Firebase, לחצו על כפתור <strong>הגלגל / הגדרות (Settings ⚙️)</strong> בלשונית Authentication &gt; לשונית <strong>Authorized domains</strong>:</p>
                    <p>1. העתיקו את הדומיין שלמעלה בעזרת כפתור "העתק".</p>
                    <p>2. לחצו ב-Firebase על <strong>Add domain</strong> והדביקו אותו.</p>
                    <p>3. מיד לאחר מכן, ההתחברות דרך Google תעבוד ישירות!</p>
                  </div>

                  <div className="pt-2 border-t border-amber-200/80">
                    <p className="text-xs font-bold text-slate-800 mb-2">רוצה להיכנס מיד ללא הגדרות נוספות?</p>
                    <button
                      type="button"
                      onClick={handleQuickAdminLogin}
                      disabled={loading}
                      className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition-all"
                    >
                      <Key size={16} className="text-amber-300" />
                      <span>התחבר עכשיו באמצעות כניסה מהירה כמנהל 🔑</span>
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col gap-3 text-red-600 bg-red-50 p-4 rounded-2xl text-sm border border-red-100"
                >
                  <div className="flex items-center gap-3">
                    <AlertCircle size={20} className="shrink-0" />
                    <span>{error}</span>
                  </div>
                  
                  {error.includes('עוגיות צד שלישי') && (
                    <button
                      onClick={openInNewTab}
                      className="mt-2 w-full bg-white hover:bg-slate-50 text-slate-700 font-bold py-2 px-4 rounded-xl border border-slate-200 transition-all text-xs"
                    >
                      פתח את האפליקציה בחלון חדש
                    </button>
                  )}
                </motion.div>
              )}
            </div>
          )}

          <div className="text-center text-slate-400 text-xs mt-6">
            <p>המערכת מוגנת ומאובטחת.</p>
            <p className="mt-1">התחבר עם חשבון ה-Google הארגוני או פרטי הגישה שלך.</p>
            
            <div className="mt-4 p-3 bg-slate-100 rounded-xl text-slate-500 text-[10px] leading-relaxed">
              <p className="font-bold mb-1">נתקלת בבעיה?</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>אפשר חלונות קופצים (Popups) בדפדפן.</li>
                <li>אפשר עוגיות צד שלישי (Third-party cookies) בהגדרות הפרטיות.</li>
                <li>נסה להשתמש בדפדפן Chrome או Edge.</li>
                <li>אם הבעיה נמשכת, פנה למנהל המערכת.</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
